import { Box, Flex, Pagination, Select, Text, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

type AppPaginationBarProps = {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Mantine Pagination `boundaries`: pages pinned at start/end (default 1). */
  boundaries?: number;
  /** Mantine Pagination `siblings`: pages beside the active page (default 1). */
  siblings?: number;
  /** When false, omit the total item count (e.g. "of 684"). Default true. */
  showTotalItems?: boolean;
  /** When false, omit the bottom-left "Results: start - end" label. Default true. */
  showResultsRange?: boolean;
};

export default function AppPaginationBar({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10],
  boundaries = 1,
  siblings = 1,
  showTotalItems = true,
  showResultsRange = true,
}: AppPaginationBarProps) {
  const theme = useMantineTheme();
  const isSmUp = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, false, {
    getInitialValueInEffect: true,
  });

  const normalizedPage = Math.max(1, Math.min(page, Math.max(totalPages, 1)));
  const start = totalItems === 0 ? 0 : (normalizedPage - 1) * pageSize + 1;
  const end = totalItems === 0 ? 0 : Math.min(normalizedPage * pageSize, totalItems);
  const canChangePageSize = !!onPageSizeChange && pageSizeOptions.length > 1;
  const effectiveSiblings = isSmUp ? siblings : 0;
  const effectiveBoundaries = isSmUp ? boundaries : 1;

  return (
    <Flex
      align={{ base: "stretch", sm: "center" }}
      gap={{ base: "xs", sm: "sm" }}
      direction={{ base: "column", sm: "row" }}
    >
      {showResultsRange ? (
        <Text
          size="sm"
          c="slate.7"
          fw={500}
          w={{ base: "100%", sm: "auto" }}
          style={{
            whiteSpace: "nowrap",
            flex: isSmUp ? 1 : undefined,
            minWidth: isSmUp ? 0 : undefined,
          }}
        >
          Results: {start} - {end}
          {showTotalItems ? ` of ${totalItems}` : ""}
        </Text>
      ) : isSmUp ? (
        <Box style={{ flex: 1, minWidth: 0 }} aria-hidden />
      ) : null}

      <Box
        w={{ base: "100%", sm: "auto" }}
        style={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
          overflowX: "auto",
          maxWidth: "100%",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Pagination
          total={Math.max(totalPages, 1)}
          value={normalizedPage}
          onChange={onPageChange}
          color="primary"
          radius="md"
          withControls
          boundaries={effectiveBoundaries}
          siblings={effectiveSiblings}
        />
      </Box>

      {canChangePageSize ? (
        <Box
          w={{ base: "100%", sm: "auto" }}
          style={{
            flex: isSmUp ? 1 : undefined,
            display: "flex",
            justifyContent: "flex-end",
            minWidth: isSmUp ? 84 : undefined,
          }}
        >
          <Select
            size="sm"
            radius="md"
            w={84}
            data={pageSizeOptions.map((value) => ({ value: String(value), label: String(value) }))}
            value={String(pageSize)}
            onChange={(value) => {
              if (!value || !onPageSizeChange) return;
              const parsed = Number(value);
              if (!Number.isNaN(parsed)) onPageSizeChange(parsed);
            }}
            disabled={!canChangePageSize}
            allowDeselect={false}
          />
        </Box>
      ) : isSmUp ? (
        <Box style={{ flex: 1, minWidth: 0 }} aria-hidden />
      ) : null}
    </Flex>
  );
}
